import { formatMoney } from '../../../util/currency';

/**
 * Label for a known extra line item (extra guests, electricity, …).
 *
 * Lived character-for-character twice, in LineItemKnownRefundMaybe and
 * LineItemUnknownRefundMaybe, which both render refund rows for the same kind of
 * item — see `docs/CODESTYLE.md`, "Duplication does not stay identical".
 *
 * @param {object} item - the line item being rendered
 * @param {object} intl - react-intl instance
 * @param {number} [quantity] - line item quantity
 * @param {number} [seats] - line item seats
 * @param {number} [units] - line item units
 * @param {number} nights - nights in the booking
 * @param {Array} [extraLineItems] - `extraLineItems` from the transaction metadata
 * @returns {string} the label, or `''` when the item has no metadata entry
 */
export const createExtraLineItemLabel = (item, intl, quantity, seats, units, nights, extraLineItems) => {
  const unitPrice = formatMoney(intl, item.unitPrice);

  // if the line item has only quantity
  if (quantity && !seats && !units) {
    return intl.formatMessage(
      { id: `BookingBreakdown.quantity.${item.code.replace('line-item/', '')}` },
      { unitPrice, quantity }
    );
  }

  // for extra guests, extra adults or extra children
  if (item.code.includes('extra')) {
    return intl.formatMessage(
      { id: `BookingBreakdown.${item.code.replace('line-item/', '')}` },
      { unitPrice, seats, units }
    );
  }

  // for extra line items that are in the metadata
  const extraLineItem = extraLineItems?.find((extraLineItem) => extraLineItem.code === item.code);

  if (!extraLineItem) return '';

  const itemLabel = intl.formatMessage(
    { id: `BookingBreakdown.${item.code.replace('line-item/', '')}` },
    { unitPrice }
  );
  const guestCountLabel = extraLineItem?.type?.perGuest
    ? `${intl.formatMessage({ id: `BookingBreakdown.guestCount` }, { count: item.seats })}`
    : '';
  const nightCountLabel = extraLineItem?.type?.perNight
    ? `${intl.formatMessage({ id: `BookingBreakdown.nightCount` }, { count: nights })}`
    : '';
  if (guestCountLabel && !nightCountLabel) {
    return `${itemLabel} * ${guestCountLabel}`;
  } else if (!guestCountLabel && nightCountLabel) {
    return `${itemLabel} * ${nightCountLabel}`;
  } else {
    return `${itemLabel} * ${guestCountLabel} * ${nightCountLabel}`;
  }
};
